import React, { useState, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  UploadFile,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { uploadSalesFile } from 'services/PointsManagementService';
import { UploadResult } from './PointsManagementType';

interface UploadFileModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && isExcelFile(droppedFile)) {
      setFile(droppedFile);
      setUploadResult(null);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const isExcelFile = (file: File): boolean => {
    const excelTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const excelExtensions = ['.xlsx', '.xls'];
    
    return (
      excelTypes.includes(file.type) ||
      excelExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    );
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const result = await uploadSalesFile(formData);
      setUploadResult(result);
      onUploadSuccess();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: '600px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Upload File Bán Hàng
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {!file ? (
          <Paper
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#1976d2',
              },
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={openFileDialog}
          >
            <CloudUpload sx={{ fontSize: 48, color: '#666', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Kéo thả file Excel vào đây
            </Typography>
            <Typography variant="body2" color="text.secondary">
              hoặc click để chọn file từ máy tính
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Hỗ trợ định dạng: .xlsx, .xls
            </Typography>
          </Paper>
        ) : (
          <Box>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <UploadFile sx={{ color: '#1976d2' }} />
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleRemove} color="error">
                <Delete />
              </IconButton>
            </Paper>

            {!uploadResult && (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={isUploading}
                fullWidth
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
              >
                {isUploading ? 'Đang upload...' : 'Upload File'}
              </Button>
            )}
          </Box>
        )}

        {uploadResult && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Kết quả upload
            </Typography>
            
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadResult.message}
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircle />}
                label={`Thành công: ${uploadResult.successCount}`}
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<Error />}
                label={`Lỗi: ${uploadResult.errorCount}`}
                color="error"
                variant="outlined"
              />
              <Chip
                label={`Tổng: ${uploadResult.totalRecords}`}
                variant="outlined"
              />
            </Box>

            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Chi tiết lỗi:
                </Typography>
                <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                  {uploadResult.errors.map((error, index) => (
                    <Typography key={index} variant="caption" display="block" color="error">
                      • {error}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Dữ liệu xử lý:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                  {JSON.stringify(uploadResult.data, null, 2)}
                </pre>
              </Paper>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            Đóng
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadFileModal;
